import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr, fileUrl } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Send, Loader2, Trash2, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function TeamChannel() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/team/channel"); setMsgs(data); }
    catch (e) { if (e?.response?.status === 402) toast.error("Team channel is a Pro feature"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    const t = setInterval(load, 15000); // light polling
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/team/channel", { body: text.trim() });
      setMsgs((m) => [...m, data]); setText("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/team/channel/${id}`); setMsgs((m) => m.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="team-channel-page" className="flex flex-col" style={{ minHeight: "calc(100vh - 8rem)" }}>
      <PageHeader title="Team Channel" subtitle="Private — only your team can see this" />
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-3"><Lock className="h-3 w-3" /> Not visible to partners</div>

      <div className="flex-1 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No messages yet. Start the conversation with your team.</p>
        ) : msgs.map((m) => {
          const mine = m.author_id === user?.id;
          return (
            <div key={m.id} data-testid="team-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "accent-bg text-white" : "bg-white shadow-[0_2px_12px_rgba(44,44,62,0.05)]"}`}>
                {!mine && <div className="text-xs font-semibold text-[var(--brand-navy)] mb-0.5">{m.author_name}</div>}
                <div className={`text-sm whitespace-pre-line ${mine ? "text-white" : "text-[#2C2C3E]"}`}>{m.body}</div>
                <div className={`text-[10px] mt-1 flex items-center gap-2 ${mine ? "text-white/60" : "text-muted-foreground"}`}>
                  {m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true }) : ""}
                  {(mine || user?.role === "admin") && <button data-testid="team-message-delete" onClick={() => del(m.id)} className="hover:opacity-80"><Trash2 className="h-3 w-3" /></button>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-20 mt-3 flex items-center gap-2 bg-cream pt-2">
        <input data-testid="team-message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Message your team…" className="flex-1 rounded-full border px-4 h-11 bg-white outline-none focus:ring-2 focus:ring-[var(--tenant-accent)]" />
        <button data-testid="team-message-send" onClick={send} disabled={sending || !text.trim()} className="h-11 w-11 rounded-full accent-bg text-white flex items-center justify-center disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
